// Avatar in the top bar. In the mobile view (utils/mobile_nav.js) it opens a menu
// with the entries that leave the bottom bar there: profile, drafts, scheduled posts,
// account settings, app settings and log out. On wider screens it links to the
// profile as before.

import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { defineMessages, useIntl } from "react-intl";

import { Link } from "react-router-dom";

import { Avatar } from "flavours/glitch/components/avatar";
import { Icon } from "flavours/glitch/components/icon";
import Permalink from "flavours/glitch/components/permalink";
import { me } from "flavours/glitch/initial_state";
import { useAppSelector } from "flavours/glitch/store";
import { parseNavigationHidden } from "flavours/glitch/utils/navigation_order";
import { useMobileNav } from "flavours/glitch/utils/mobile_nav";

const messages = defineMessages({
  menu: { id: "account_menu.label", defaultMessage: "Account menu" },
  profile: { id: "account_menu.profile", defaultMessage: "Profile" },
  account_settings: { id: "navigation_bar.account_settings", defaultMessage: "Account settings" },
  app_settings: { id: "navigation_bar.app_settings", defaultMessage: "App settings" },
  drafts: { id: "navigation_bar.drafts", defaultMessage: "Drafts" },
  scheduled_statuses: { id: "navigation_bar.scheduled_statuses", defaultMessage: "Scheduled posts" },
  logout: { id: "navigation_bar.logout", defaultMessage: "Log out" },
});

// Same order as AVATAR_MENU_ITEMS (utils/mobile_nav.js), log out follows.
const ENTRIES = [
  { id: "drafts", to: "/drafts", icon: "floppy-disk" },
  { id: "scheduled_statuses", to: "/scheduled_statuses", icon: "clock" },
  { id: "account_settings", to: "/account-settings", icon: "user-circle" },
  { id: "app_settings", to: "/app-settings", icon: "gear" },
];

export const AccountMenu = ({ onLogout }) => {
  const intl = useIntl();
  const mobile = useMobileNav();
  const account = useAppSelector(state => state.getIn(["accounts", me]));
  const hidden = parseNavigationHidden(useAppSelector(state => state.getIn(["local_settings", "navigation_hidden"])));
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutside = (e) => {
      if (!rootRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        rootRef.current?.querySelector(".account-menu__button")?.focus();
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleKey);
    listRef.current?.querySelector("[role='menuitem']")?.focus();

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  // Leaving the mobile view closes the menu.
  if (!mobile && open) {
    setOpen(false);
  }

  const handleToggle = useCallback(() => setOpen(value => !value), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleLogout = useCallback(() => {
    setOpen(false);
    onLogout();
  }, [onLogout]);

  const handleMenuKey = useCallback((e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
      return;
    }
    e.preventDefault();
    const items = [...listRef.current.querySelectorAll("[role='menuitem']")];
    const index = items.indexOf(document.activeElement);
    items[(index + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
  }, []);

  if (!account) {
    return null;
  }

  if (!mobile) {
    return (
      <Permalink href={account.get("url")} to={`/@${account.get("acct")}`} title={account.get("acct")}>
        <Avatar account={account} size={34} />
      </Permalink>
    );
  }

  return (
    <div className='account-menu' ref={rootRef}>
      <button
        type='button'
        className='account-menu__button'
        onClick={handleToggle}
        aria-haspopup='menu'
        aria-expanded={open}
        aria-label={intl.formatMessage(messages.menu)}
        title={account.get("acct")}
      >
        <Avatar account={account} size={34} />
      </button>

      {/* Rendered into <body>: the top bar clips its content and sits below the column title */}
      {open && createPortal(
        <div className='account-menu__list' ref={listRef} role='menu' tabIndex={-1} aria-label={intl.formatMessage(messages.menu)} onKeyDown={handleMenuKey}>
          <Link role='menuitem' className='account-menu__profile' to={`/@${account.get("acct")}`} onClick={handleClose}>
            <Avatar account={account} size={40} />
            <span>
              <strong>{account.get("display_name") || account.get("username")}</strong>
              <span className='account-menu__acct'>@{account.get("acct")}</span>
            </span>
          </Link>

          <hr />

          {ENTRIES.filter(entry => !hidden.includes(entry.id)).map(entry => (
            <Link key={entry.id} role='menuitem' to={entry.to} onClick={handleClose}>
              <Icon id={entry.icon} />
              {intl.formatMessage(messages[entry.id])}
            </Link>
          ))}

          <hr />

          <button type='button' role='menuitem' onClick={handleLogout}>
            <Icon id='sign-out' />
            {intl.formatMessage(messages.logout)}
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
};

AccountMenu.propTypes = {
  onLogout: PropTypes.func.isRequired,
};
